import React from "react";

import _ from "lodash";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "react-query";

import useCartQuery from "api/hooks/useCartQuery";
import useQueryKeyBuilder from "api/hooks/useQueryKeyBuilder";
import ErrorCodeTranslation from "components/ErrorCodeTranslation";
import { CartStatus } from "constants/cart";
import useOrder from "hooks/useOrder";
import { useAPI } from "libs/api";
import { I18n } from "libs/i18n";
import { useMessage } from "libs/message";
import Cart from "models/Cart";

import CartContext from "./CartContext";

const CartContextProvider = ({ children = null }) => {
  const api = useAPI("WeezPay");
  const queryClient = useQueryClient();
  const queryKeyBuilder = useQueryKeyBuilder();
  const { error: messageError } = useMessage();
  const { cartUUID, setCartUUID, walletId, refundWalletId } = useOrder();
  const [isInitializing, setIsInitializing] = React.useState(true);
  const [cart, setCart] = React.useState({
    uuid: null,
    rowGroups: [],
    walletId: refundWalletId || walletId,
  });

  const [lastCart, setLastCart] = React.useState(cart);
  const [lastAPICall, setLastAPICall] = React.useState(null);

  const {
    data: cartFetchedData,
    isLoading: cartFetchedIsLoading,
    error: cartError,
    refetch: cartRefetch,
  } = useCartQuery(
    cartUUID,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!cartUUID,
      retry: 1,
    },
  );
  const hasCartExpired = cartError && cartError.status === 404;

  React.useEffect(
    () => {
      if (hasCartExpired) {
        setCartUUID(null);
      }
    },
    [hasCartExpired, setCartUUID],
  );

  // At first retrieve, if the cart has already been validated or does not belong to the current wallet,
  // then we should flush the memory.
  React.useEffect(
    () => {
      const emptyCart = {
        uuid: null,
        rowGroups: [],
        walletId: refundWalletId || walletId,
      };
      if (cartFetchedData) {
        if (cartFetchedData?.status === CartStatus.VALIDATED) {
          setIsInitializing(false);
          setCart(emptyCart);
          setLastCart(emptyCart);
          setCartUUID(null);
        } else if (cartFetchedData?.walletId === (refundWalletId || walletId)) {
          setIsInitializing(false);
          setCart(cartFetchedData);
          setLastCart(cartFetchedData);
        }
      } else if (!cartFetchedIsLoading) {
        setIsInitializing(false);
        setCart(emptyCart);
        setLastCart(emptyCart);
      }
    },
    [cartFetchedData, cartFetchedIsLoading, setCartUUID, walletId, refundWalletId],
  );

  React.useEffect(
    () => {
      if ((cart?.uuid && (cart?.walletId !== (refundWalletId || walletId)))) {
        const emptyCart = {
          uuid: null,
          rowGroups: [],
          walletId: refundWalletId || walletId,
        };
        setIsInitializing(false);
        setCart(emptyCart);
        setLastCart(emptyCart);
      }
    },
    [walletId, cart, refundWalletId, cartFetchedData],
  );

  const { mutateAsync: cartCreate, ...cartCreateMutation } = useMutation(
    (data) => api.carts.create(data),
  );
  const { mutateAsync: cartUpdate, ...cartUpdateMutation } = useMutation(
    ({ uuid, data }) => api.carts.partialUpdate(uuid, data),
  );

  const handleError = React.useCallback(
    async (err) => {
      const emptyCart = {
        uuid: null,
        rowGroups: [],
        walletId: refundWalletId || walletId,
      };

      let errors = {};
      if (err.status === 404) {
        setCart(emptyCart);
        setLastCart(emptyCart);
        setCartUUID(null);
        messageError(<I18n id="cart-page-cart-error-expired" />);
        return undefined;
      }
      if (err.status === 400) {
        const errorBody = await err.json();
        errors = errorBody;
      }

      if (!Object.prototype.hasOwnProperty.call(errors, "errors")) {
        messageError(<I18n id="global-error-unknown" />);
        return undefined;
      }
      errors.errors.forEach((error) => {
        if (error.code === "readonly_cart") {
          setCart(emptyCart);
          setLastCart(emptyCart);
          setCartUUID(null);
          messageError(<I18n id="global-error-unknown" />);
          return undefined;
        }
        const translatedError = (
          <ErrorCodeTranslation
            errorCode={error.code}
            errorDetails={error.details || {}}
            defaultMessage={<I18n id="global-error-unknown" />}
          />
        );
        messageError(translatedError);
        return undefined;
      });
      return undefined;
    },
    [messageError, setCartUUID, walletId, refundWalletId],
  );

  const createCart = React.useCallback(
    async (cartData, shouldHandleError) => {
      setLastAPICall("CREATE");
      return cartCreate(Cart.toAPI(cartData))
        .then((newCart) => {
          const newCartMapped = Cart.fromAPI(newCart);
          queryClient.setQueryData(queryKeyBuilder(["carts", newCart.uuid]), newCart);
          setLastCart(newCartMapped);
          setCart(newCartMapped);
          setCartUUID(newCartMapped.uuid);
          return Promise.resolve(newCart);
        })
        .catch((error) => {
          setCart(lastCart);
          if (shouldHandleError) {
            handleError(error);
          }
          return Promise.reject(error);
        });
    },
    [cartCreate, handleError, lastCart, setCartUUID, queryKeyBuilder, queryClient],
  );

  const updateCart = React.useCallback(
    async (cartData, shouldHandleError) => {
      setLastAPICall("UPDATE");
      return cartUpdate({ uuid: cartData.uuid, data: Cart.toAPI(cartData) })
        .then((updatedCart) => {
          const updatedCartMapped = Cart.fromAPI(updatedCart);
          queryClient.setQueryData(queryKeyBuilder(["carts", updatedCart.uuid]), updatedCart);
          setLastCart(updatedCartMapped);
          setCart(updatedCartMapped);
          setCartUUID(updatedCartMapped.uuid);
          return Promise.resolve(updatedCart);
        })
        .catch((error) => {
          setCart(lastCart);
          if (shouldHandleError) {
            handleError(error);
          }
          return Promise.reject(error);
        });
    },
    [cartUpdate, handleError, lastCart, setCartUUID, queryKeyBuilder, queryClient],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCreateCart = React.useCallback(
    _.debounce(createCart, 600),
    [createCart],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateCart = React.useCallback(
    _.debounce(updateCart, 600),
    [updateCart],
  );

  const createOrUpdateCart = React.useCallback(
    async (cartData, shouldDebounce = true, shouldHandleError = true) => {
      if (cartData.uuid) {
        return shouldDebounce
          ? debouncedUpdateCart(cartData, shouldHandleError)
          : updateCart(cartData, shouldHandleError);
      }
      return shouldDebounce
        ? debouncedCreateCart(cartData, shouldHandleError)
        : createCart(cartData, shouldHandleError);
    },
    [debouncedCreateCart, createCart, debouncedUpdateCart, updateCart],
  );

  let isLoading = cartFetchedIsLoading;
  let isError = false;
  let isSuccess = false;
  let error = null;
  if (lastAPICall === "CREATE") {
    isLoading = cartCreateMutation?.isLoading || cartFetchedIsLoading;
    isError = cartCreateMutation?.isError;
    isSuccess = cartCreateMutation?.isSuccess;
    error = cartCreateMutation?.error;
  } else if (lastAPICall === "UPDATE") {
    isLoading = cartUpdateMutation?.isLoading || cartFetchedIsLoading;
    isError = cartUpdateMutation?.isError;
    isSuccess = cartUpdateMutation?.isSuccess;
    error = cartUpdateMutation?.error;
  }

  const value = {
    cart, // The internal state of the cart.
    setCart, // To update the internal state of the cart.
    createOrUpdateCart, // To call API with data to update cart.
    isEmpty: !isInitializing && !isLoading && !cart?.rowGroups?.length,
    isInitializing,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch: cartRefetch,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

CartContextProvider.propTypes = {
  children: PropTypes.node,
};

export default CartContextProvider;
